import React from "react";
import {
  Box,
  Option,
  Avatar,
  Stack,
  Modal,
  ModalDialog,
  ModalClose,
  Tooltip,
  Chip,
} from "@mui/joy";
import dayjs from "dayjs";
import type {
  Equipment,
  EquipmentModel,
  EquipmentAttributeDefinition,
  AttributeOption,
  AttributeType,
} from "../../../api/equipment/Equipment.typed";
import { useAuth } from "../../../contexts/api";
import { userDisplayName } from "../../../utils/userHelper";
import {
  SComment,
  SDelete,
  SEdit,
  SClose,
  SDone,
  SProduct,
  QrCodeIcon,
  DownloadIcon,
  RefreshIcon,
  SDownload,
  SRefresh,
} from "../../../styles/StyledMUI";
import DarkSelect from "../../common/DarkSelect";
import DarkDatePicker from "../../common/DarkDatePicker";
import NumericInput from "../../common/NumericInput";
import DarkInput from "../../common/DarkInput";
import DarkCheckbox from "../../common/DarkCheckbox";
import { usePermissions, Permission } from "../../../contexts/app/permissions";
import { findColorName } from "../../../utils/colorPalette";
import { rentalStatusChipColor, rentalStatusLabel } from "../../rental/rentalStatusHelpers";
import CommentsModal from "../../common/CommentsModal";
import type { MultiComment, Comment } from "../../../api/zekay/comments";
import IconButtonCTA from "../../common/IconButtonCTA";
import {
  colorTokens,
  equipmentCardsAttributeCellSx,
  equipmentCardsAttributeGridSx,
  equipmentCardsAttributeLabelSx,
  equipmentCardsEditControlSlotSx,
  listRowAlertBg,
  listRowAlertBgHover,
  listRowAlertBorder,
  textTokens,
  withAlpha,
} from "../../../styles/globalStyles";
import DarkTypography from "../../common/DarkTypography";
import ListRowAlertBanner from "../../common/ListRowAlertBanner";
import { computeEquipmentAlertLabels } from "../../../utils/equipmentAlertHelpers";

interface EquipmentCardsProps {
  selectedModel: EquipmentModel;
  equipments: Equipment[];
  selectedIds: string[];
  editingIndex: number | null;
  editItem: Equipment | null;
  qrMap: Record<string, string>;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onEditEquipment: (index: number, equipment: Equipment) => void;
  onDeleteEquipment: (id: string) => void;
  onSaveEquipment: (equipment?: Equipment, keepEditing?: boolean) => void;
  /** True while create/update API is in flight (avoids double POST on rapid clicks). */
  saveEquipmentPending?: boolean;
  onCancelEdit: () => void;
  onEditItemChange: (equipment: Equipment) => void;
  hasActiveFilters?: boolean;
  onOpenExport?: (id: string) => void;
  onOpenRegen?: (id: string) => void;
  onOpenSelectionExport?: () => void;
  onOpenSelectionRegen?: () => void;
}

const EquipmentCards: React.FC<EquipmentCardsProps> = ({
  selectedModel,
  equipments,
  selectedIds,
  editingIndex,
  editItem,
  qrMap,
  onToggleSelect,
  onToggleSelectAll,
  onEditEquipment,
  onDeleteEquipment,
  onSaveEquipment,
  saveEquipmentPending = false,
  onCancelEdit,
  onEditItemChange,
  hasActiveFilters,
  onOpenExport,
  onOpenRegen,
  onOpenSelectionExport,
  onOpenSelectionRegen,
}) => {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.EQUIPMENT_EDIT);
  const canSeeRentalStatus = hasPermission(Permission.RENTAL_VIEW);
  const { user } = useAuth();
  const [qrModalOpen, setQrModalOpen] = React.useState(false);
  const [selectedQrCode, setSelectedQrCode] = React.useState<string>("");
  const [selectedEquipmentId, setSelectedEquipmentId] = React.useState<string>("");

  const [commentsEditor, setCommentsEditor] = React.useState<{
    open: boolean;
    equipmentIndex: number | null;
    attrId: string | null;
    attrLabel: string;
    comments: Comment[];
    newComment: string;
  }>({
    open: false,
    equipmentIndex: null,
    attrId: null,
    attrLabel: "",
    comments: [],
    newComment: "",
  });
  const [equipmentModalComments, setEquipmentModalComments] = React.useState<
    MultiComment[] | undefined
  >(undefined);

  const handleQrClick = (equipmentId: string, qrCode: string) => {
    setSelectedQrCode(qrCode);
    setSelectedEquipmentId(equipmentId);
    setQrModalOpen(true);
  };

  const openCommentsEditor = (
    equipmentIndex: number,
    attr: EquipmentAttributeDefinition,
    value: any,
  ) => {
    const existing: Comment[] = Array.isArray(value) ? value : [];
    setCommentsEditor({
      open: true,
      equipmentIndex,
      attrId: attr.ID,
      attrLabel: attr.Label,
      comments: existing,
      newComment: "",
    });
    // Bridge to MultiComment for shared CommentsModal
    const toMulti = (list: Comment[]): MultiComment[] =>
      list.map((c) => ({ User: c.user, Comment: c.comment, Date: c.timestamp }));
    setEquipmentModalComments(toMulti(existing));
  };

  const closeCommentsEditor = () => {
    setCommentsEditor((prev) => ({ ...prev, open: false }));
    setEquipmentModalComments(undefined);
  };

  const renderAttributeInput = (
    attr: EquipmentAttributeDefinition,
    value: any,
    equipmentIndex: number,
  ) => {
    if (!editItem) return null;

    const handleChange = (newValue: string | number) => {
      const updated = {
        ...editItem,
        Attributes: { ...editItem.Attributes, [attr.ID]: newValue },
      };
      onEditItemChange(updated);
    };

    switch (attr.Type as AttributeType) {
      case "string":
        return (
          <DarkInput
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            size="sm"
            sx={equipmentCardsEditControlSlotSx}
          />
        );
      case "number":
        return (
          <NumericInput
            mode="decimal"
            allowNegative
            value={value || ""}
            onValueChange={(next) => handleChange(next)}
            size="sm"
            sx={equipmentCardsEditControlSlotSx}
          />
        );
      case "date":
        return (
          <DarkDatePicker
            value={value || ""}
            onChange={handleChange}
            size="sm"
            sx={equipmentCardsEditControlSlotSx}
          />
        );
      case "list":
        return (
          <DarkSelect
            value={value || ""}
            onChange={(_, newValue) => handleChange(newValue)}
            size="sm"
            sx={equipmentCardsEditControlSlotSx}
          >
            {attr.Options?.map((opt: AttributeOption) => (
              <Option key={opt.ID} value={opt.Label}>
                {opt.Label}
              </Option>
            ))}
          </DarkSelect>
        );
      case "comments": {
        const count = Array.isArray(value) ? value.length : 0;
        return (
          <Box
            sx={{
              ...equipmentCardsEditControlSlotSx,
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Box onClick={() => openCommentsEditor(equipmentIndex, attr, value)}>
              <SComment iconColor={colorTokens.accent.primary} />
            </Box>
            <Chip
              size="sm"
              variant="soft"
              color="neutral"
              sx={{
                backgroundColor: colorTokens.bg.surfaceMuted,
                color: colorTokens.text.primary,
                px: 0.75,
              }}
            >
              {count}
            </Chip>
          </Box>
        );
      }
      default:
        return (
          <DarkInput
            value={value || ""}
            onChange={(e) => handleChange(e.target.value)}
            size="sm"
            sx={equipmentCardsEditControlSlotSx}
          />
        );
    }
  };

  const formatAttributeValue = (attr: EquipmentAttributeDefinition, value: any) => {
    if (!value) return "—";

    switch (attr.Type as AttributeType) {
      case "date": {
        const target = dayjs(value);

        return target.isValid() ? target.format("DD/MM/YYYY") : String(value);
      }
      case "list":
        return String(value);
      case "comments":
        if (Array.isArray(value)) {
          return value.length > 0 ? `${value.length} commentaire(s)` : "";
        }

        return "";
      default:
        return String(value);
    }
  };

  const isEditing = (index: number) => editingIndex === index;
  const isSelected = (id: string) => selectedIds.includes(id);

  const alertLabelsByIndex = React.useMemo(
    () =>
      equipments.map((eq, idx) =>
        computeEquipmentAlertLabels(
          editingIndex === idx && editItem ? editItem : eq,
          selectedModel,
        ),
      ),
    [equipments, editingIndex, editItem, selectedModel],
  );

  if (equipments.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${colorTokens.border.default}`,
            background: `linear-gradient(90deg, ${colorTokens.bg.surfaceMuted} 0%, ${colorTokens.bg.surface} 100%)`,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <DarkCheckbox
                checked={false}
                disabled
                size="sm"
                sx={{
                  color: textTokens.color.primary,
                  "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.1)" },
                }}
              />
              <DarkTypography
                level="body-sm"
                sx={{ fontWeight: 600, color: colorTokens.text.primary }}
              >
                Tout sélectionner
              </DarkTypography>
            </Box>
            <Box
              sx={{
                background: colorTokens.bg.surfaceMuted,
                color: colorTokens.text.primary,
                padding: "2px 6px",
                borderRadius: "8px",
                fontSize: "0.7rem",
                fontWeight: 500,
              }}
            >
              0 équipement
            </Box>
          </Box>
        </Box>

        {/* Empty area - fills remaining space */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 3,
            color: colorTokens.text.primary,
            background: colorTokens.bg.app,
            minHeight: 0,
          }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              backgroundColor: colorTokens.bg.surfaceMuted,
              color: colorTokens.text.muted,
            }}
          >
            📦
          </Avatar>
          <Stack spacing={1} alignItems="center">
            <DarkTypography level="h4" sx={{ color: colorTokens.text.primary, fontWeight: 500 }}>
              {hasActiveFilters ? "Aucun résultat" : "Aucun équipement"}
            </DarkTypography>
            <DarkTypography level="body-sm" colorVariant="muted" sx={{ textAlign: "center" }}>
              {hasActiveFilters
                ? "Essayez de modifier vos critères de recherche"
                : "Commencez par ajouter votre premier équipement"}
            </DarkTypography>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Modern unified header - fixed height */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${colorTokens.border.default}`,
          background: `linear-gradient(90deg, ${colorTokens.bg.surfaceMuted} 0%, ${colorTokens.bg.surface} 100%)`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <DarkCheckbox
              checked={
                selectedIds.length === equipments.filter((e) => e.ID).length &&
                equipments.filter((e) => e.ID).length > 0
              }
              indeterminate={
                selectedIds.length > 0 && selectedIds.length < equipments.filter((e) => e.ID).length
              }
              onChange={onToggleSelectAll}
              size="sm"
              sx={{
                color: textTokens.color.primary,
                "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.1)" },
              }}
            />
            <DarkTypography
              level="body-sm"
              sx={{ fontWeight: 600, color: colorTokens.text.primary }}
            >
              {selectedIds.length > 0
                ? `${selectedIds.length} sélectionné${selectedIds.length > 1 ? "s" : ""}`
                : "Tout sélectionner"}
            </DarkTypography>
          </Box>
          {canManage &&
            selectedIds.length > 0 &&
            (onOpenSelectionExport || onOpenSelectionRegen) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                {onOpenSelectionExport && (
                  <Tooltip title="Exporter les QR (sélection)">
                    <IconButtonCTA
                      ctaVariant="neutral"
                      size="sm"
                      aria-label="Exporter les QR de la sélection"
                      onClick={onOpenSelectionExport}
                      sx={{ minHeight: 32, minWidth: 32 }}
                    >
                      <SDownload sx={{ fontSize: 18 }} />
                    </IconButtonCTA>
                  </Tooltip>
                )}
                {onOpenSelectionRegen && (
                  <Tooltip title="Régénérer les QR (sélection)">
                    <IconButtonCTA
                      ctaVariant="accent"
                      size="sm"
                      aria-label="Régénérer les QR de la sélection"
                      onClick={onOpenSelectionRegen}
                      sx={{ minHeight: 32, minWidth: 32 }}
                    >
                      <SRefresh sx={{ fontSize: 18 }} />
                    </IconButtonCTA>
                  </Tooltip>
                )}
              </Box>
            )}
        </Box>
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          px: 1,
          py: 1,
          background: colorTokens.bg.app,
          minHeight: 0,
        }}
      >
        <Stack spacing={0.5}>
          {equipments.map((equipment, index) => (
            <Box
              key={equipment.ID || index}
              sx={{
                background: alertLabelsByIndex[index]?.length
                  ? listRowAlertBg
                  : `linear-gradient(135deg, ${colorTokens.bg.surface} 0%, ${colorTokens.bg.surfaceMuted} 100%)`,
                border: isSelected(equipment.ID!)
                  ? `2px solid ${colorTokens.accent.primary}`
                  : alertLabelsByIndex[index]?.length
                    ? `1px solid ${listRowAlertBorder}`
                    : `1px solid ${colorTokens.border.default}`,
                borderRadius: "8px",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  background: alertLabelsByIndex[index]?.length
                    ? listRowAlertBgHover
                    : `linear-gradient(135deg, ${colorTokens.bg.surfaceElevated} 0%, ${colorTokens.bg.surfaceMuted} 100%)`,
                  borderColor: isSelected(equipment.ID!)
                    ? colorTokens.accent.primary
                    : alertLabelsByIndex[index]?.length
                      ? listRowAlertBorder
                      : colorTokens.border.strong,
                },
              }}
            >
              <Box sx={{ p: 1 }}>
                <ListRowAlertBanner labels={alertLabelsByIndex[index] ?? []} />
                {/* Header row compacte */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  {/* Left: Selection + Meta compact */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {equipment.ID && (
                      <DarkCheckbox
                        checked={isSelected(equipment.ID)}
                        onChange={() => onToggleSelect(equipment.ID!)}
                        size="sm"
                        sx={{
                          color: textTokens.color.primary,
                          "&:hover": { backgroundColor: "rgba(33, 150, 243, 0.1)" },
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.3,
                        flexWrap: "wrap",
                        minWidth: 0,
                      }}
                    >
                      {(equipment.QRCode || qrMap[equipment.ID!]) && (
                        <Box
                          onClick={() => {
                            const qrSrc =
                              typeof equipment.QRCode === "string"
                                ? equipment.QRCode
                                : equipment.QRCode?.QRCode;
                            handleQrClick(equipment.ID!, qrSrc || qrMap[equipment.ID!]);
                          }}
                          sx={{
                            background: "#ff9800",
                            color: textTokens.color.primary,
                            padding: "2px 6px",
                            borderRadius: "8px",
                            fontSize: "0.65rem",
                            fontWeight: 600,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                            flexShrink: 0,
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              background: "#f57c00",
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <QrCodeIcon sx={{ fontSize: 10 }} />
                          QR
                        </Box>
                      )}
                      {equipment.ID && canManage && onOpenExport && (
                        <Tooltip title="Exporter le QR code">
                          <Box
                            sx={{
                              display: "inline-flex",
                              flexShrink: 0,
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: 28,
                              minHeight: 28,
                            }}
                          >
                            <IconButtonCTA
                              ctaVariant="neutral"
                              aria-label="Exporter le QR code"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenExport(equipment.ID!);
                              }}
                              sx={{
                                minHeight: "28px",
                                minWidth: "28px",
                                flexShrink: 0,
                              }}
                            >
                              <DownloadIcon sx={{ fontSize: 16 }} />
                            </IconButtonCTA>
                          </Box>
                        </Tooltip>
                      )}
                      {equipment.ID && canManage && onOpenRegen && (
                        <Tooltip title="Régénérer le QR code">
                          <Box
                            sx={{
                              display: "inline-flex",
                              flexShrink: 0,
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: 28,
                              minHeight: 28,
                            }}
                          >
                            <IconButtonCTA
                              ctaVariant="accent"
                              aria-label="Régénérer le QR code"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenRegen(equipment.ID!);
                              }}
                              sx={{
                                minHeight: "28px",
                                minWidth: "28px",
                                flexShrink: 0,
                                "&:hover": {
                                  backgroundColor: withAlpha(colorTokens.accent.primary, 0.1),
                                },
                              }}
                            >
                              <RefreshIcon sx={{ fontSize: 16 }} />
                            </IconButtonCTA>
                          </Box>
                        </Tooltip>
                      )}
                      {typeof equipment.QRCode === "object" &&
                        equipment.QRCode?.BackgroundColor && (
                          <Tooltip
                            title={
                              findColorName(equipment.QRCode.BackgroundColor) ||
                              equipment.QRCode.BackgroundColor
                            }
                          >
                            <Box
                              sx={{
                                display: "inline-flex",
                                flexShrink: 0,
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 28,
                                minHeight: 28,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  backgroundColor: equipment.QRCode.BackgroundColor,
                                  border: "1px solid #444",
                                  flexShrink: 0,
                                }}
                              />
                            </Box>
                          </Tooltip>
                        )}
                      {canSeeRentalStatus && equipment.rental_status && (
                        <Tooltip title={rentalStatusLabel(equipment.rental_status)}>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={rentalStatusChipColor(equipment.rental_status)}
                            sx={{
                              flexShrink: 0,
                              px: 0.5,
                              "--Chip-paddingInline": "6px",
                            }}
                          >
                            <SProduct sx={{ fontSize: 14 }} />
                          </Chip>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>

                  {/* Right: Actions */}
                  <Box sx={{ display: "flex", gap: 0.3 }}>
                    {isEditing(index) ? (
                      <>
                        <IconButtonCTA
                          ctaVariant="success"
                          aria-label="Sauvegarder équipement"
                          size="sm"
                          disabled={saveEquipmentPending}
                          onClick={() => onSaveEquipment()}
                          sx={{
                            minHeight: "28px",
                            minWidth: "28px",
                            "&:hover": {
                              backgroundColor: withAlpha(colorTokens.status.success, 0.1),
                            },
                          }}
                        >
                          <SDone />
                        </IconButtonCTA>
                        <IconButtonCTA
                          ctaVariant="danger"
                          aria-label="Annuler édition équipement"
                          size="sm"
                          onClick={onCancelEdit}
                          sx={{
                            minHeight: "28px",
                            minWidth: "28px",
                            "&:hover": {
                              backgroundColor: withAlpha(colorTokens.status.danger, 0.1),
                            },
                          }}
                        >
                          <SClose />
                        </IconButtonCTA>
                      </>
                    ) : (
                      <>
                        {/* Comments entry button removed to avoid duplication */}
                        {canManage && (
                          <IconButtonCTA
                            ctaVariant="accent"
                            aria-label="Modifier équipement"
                            size="sm"
                            onClick={() => onEditEquipment(index, equipment)}
                            sx={{
                              minHeight: "24px",
                              minWidth: "24px",
                              p: 0.1,
                              "&:hover": {
                                backgroundColor: withAlpha(colorTokens.accent.primary, 0.1),
                              },
                            }}
                          >
                            <SEdit />
                          </IconButtonCTA>
                        )}
                        {canManage && equipment.ID && (
                          <IconButtonCTA
                            ctaVariant="danger"
                            aria-label="Supprimer équipement"
                            size="sm"
                            onClick={() => onDeleteEquipment(equipment.ID!)}
                            sx={{
                              minHeight: "24px",
                              minWidth: "24px",
                              p: 0.1,
                              "&:hover": {
                                backgroundColor: withAlpha(colorTokens.status.danger, 0.1),
                              },
                            }}
                          >
                            <SDelete />
                          </IconButtonCTA>
                        )}
                      </>
                    )}
                  </Box>
                </Box>

                {/* Attributes grid compact */}
                <Box sx={equipmentCardsAttributeGridSx}>
                  {selectedModel.Attributes.map((attr: EquipmentAttributeDefinition) => {
                    const value =
                      isEditing(index) && editItem
                        ? editItem.Attributes?.[attr.ID]
                        : equipment.Attributes?.[attr.ID];

                    return (
                      <Box key={attr.ID} sx={equipmentCardsAttributeCellSx}>
                        <DarkTypography
                          level="body-xs"
                          colorVariant="muted"
                          sx={{
                            ...equipmentCardsAttributeLabelSx,
                            fontSize: "0.65rem",
                            wordBreak: "break-word",
                            textTransform: "uppercase",
                          }}
                        >
                          {attr.Label}
                        </DarkTypography>
                        {isEditing(index) ? (
                          renderAttributeInput(attr, value, index)
                        ) : (
                          <Box
                            sx={{
                              ...equipmentCardsEditControlSlotSx,
                              color: textTokens.color.primary,
                              fontWeight: 400,
                              lineHeight: 1.3,
                              fontSize: "0.8rem",
                              textOverflow: "ellipsis",
                              overflow: "hidden",
                              whiteSpace: "nowrap",
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            {attr.Type === "comments" ? (
                              <Box
                                onClick={() => openCommentsEditor(index, attr, value)}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  cursor: "pointer",
                                }}
                              >
                                <SComment iconColor="#9e9e9e" />
                                <Chip
                                  size="sm"
                                  variant="soft"
                                  color="neutral"
                                  sx={{
                                    backgroundColor: "#404040",
                                    color: textTokens.color.primary,
                                    px: 0.75,
                                  }}
                                >
                                  {Array.isArray(value) ? value.length : 0}
                                </Chip>
                              </Box>
                            ) : (
                              formatAttributeValue(attr, value)
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Modal QR Code */}
      <Modal open={qrModalOpen} onClose={() => setQrModalOpen(false)}>
        <ModalDialog
          size="sm"
          sx={{
            backgroundColor: colorTokens.bg.surface,
            border: `1px solid ${colorTokens.border.default}`,
            maxWidth: "400px",
          }}
        >
          <ModalClose sx={{ color: textTokens.color.primary }} />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              p: 2,
            }}
          >
            <DarkTypography level="h4" colorVariant="title" sx={{ textAlign: "center" }}>
              QR Code - {selectedEquipmentId.slice(-6).toUpperCase()}
            </DarkTypography>
            {selectedQrCode && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={selectedQrCode}
                  alt="QR Code"
                  style={{
                    width: "200px",
                    height: "200px",
                    display: "block",
                  }}
                />
              </Box>
            )}
          </Box>
        </ModalDialog>
      </Modal>

      {/* Comments modal (shared) */}
      <CommentsModal
        open={commentsEditor.open}
        title={commentsEditor.attrLabel}
        currentUserName={userDisplayName(user)}
        multiComments={equipmentModalComments}
        setMultiComments={setEquipmentModalComments}
        onClose={closeCommentsEditor}
        allowEdit={
          canManage &&
          commentsEditor.equipmentIndex !== null &&
          isEditing(commentsEditor.equipmentIndex)
        }
        onSave={(updated) => {
          const toEquip = (list: MultiComment[] | undefined): Comment[] =>
            (list || []).map((m) => ({ user: m.User, comment: m.Comment, timestamp: m.Date }));
          const mapped = toEquip(updated);
          setCommentsEditor((prev) => ({ ...prev, comments: mapped }));
          if (editItem && commentsEditor.attrId) {
            const updatedEquipment = {
              ...editItem,
              Attributes: { ...(editItem.Attributes || {}), [commentsEditor.attrId]: mapped },
            } as Equipment;
            onEditItemChange(updatedEquipment);
            if (updatedEquipment.ID) {
              onSaveEquipment(updatedEquipment, true);
            }
          }
        }}
      />
    </Box>
  );
};

export default React.memo(EquipmentCards);
